export type AuthField =
  | "fullName"
  | "email"
  | "password"
  | "confirmPassword"
  | "governorate"
  | "birthday"
  | "phoneNumber"
  | "educationLevel"
  | "role";

export interface AuthApiError {
  field?: AuthField;
  message: string;
}

const collectMessages = (value: unknown): string[] => {
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.flatMap(collectMessages);
  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).flatMap(collectMessages);
  }
  return [];
};

const getResponseMessages = (error: unknown) => {
  const response = (error as {
    response?: { data?: unknown };
  })?.response;

  if (!response) return [];
  if (typeof response.data === "string") return [response.data];
  if (!response.data || typeof response.data !== "object") return [];

  const payload = response.data as Record<string, unknown>;
  return [
    ...collectMessages(payload.errors),
    ...collectMessages(payload.message),
    ...collectMessages(payload.data),
  ];
};

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export const getLoginError = (error: unknown): AuthApiError => {
  const messages = getResponseMessages(error);
  const combined = normalize(messages.join(" "));

  if (!messages.length) {
    return { message: "Impossible de contacter le serveur. Veuillez réessayer." };
  }

  if (
    combined.includes("bad credentials") ||
    combined.includes("identifiants incorrects") ||
    combined.includes("e-mail ou mot de passe incorrect") ||
    combined.includes("resource not found")
  ) {
    return {
      field: "password",
      message: "Adresse e-mail ou mot de passe incorrect.",
    };
  }

  if (combined.includes("email") || combined.includes("e-mail")) {
    return { field: "email", message: "Veuillez vérifier votre adresse e-mail." };
  }

  if (combined.includes("password") || combined.includes("mot de passe")) {
    return { field: "password", message: "Veuillez vérifier votre mot de passe." };
  }

  return { message: messages[0] || "La connexion a échoué. Veuillez réessayer." };
};

export const getRegistrationError = (error: unknown): AuthApiError => {
  const messages = getResponseMessages(error);
  const combined = normalize(messages.join(" "));

  if (!messages.length) {
    return { message: "Impossible de contacter le serveur. Veuillez réessayer." };
  }

  if (
    combined.includes("phonenumber") ||
    combined.includes("phone number") ||
    combined.includes("telephone")
  ) {
    return {
      field: "phoneNumber",
      message: combined.includes("already") || combined.includes("deja")
        ? "Ce numéro de téléphone est déjà utilisé."
        : "Veuillez vérifier le numéro de téléphone.",
    };
  }

  if (combined.includes("email") || combined.includes("e-mail")) {
    return {
      field: "email",
      message: combined.includes("already") || combined.includes("deja") || combined.includes("invalid email")
        ? "Cette adresse e-mail est déjà utilisée."
        : "Veuillez vérifier l’adresse e-mail.",
    };
  }

  const fieldMatchers: Array<[AuthField, RegExp, string]> = [
    ["fullName", /fullname|nom complet/, "Veuillez vérifier le nom complet."],
    ["password", /password|mot de passe/, "Veuillez vérifier le mot de passe."],
    ["governorate", /governorate|gouvernorat/, "Veuillez sélectionner un gouvernorat valide."],
    ["birthday", /birthday|date de naissance/, "Veuillez vérifier la date de naissance."],
    ["educationLevel", /educationlevel|classe|niveau/, "Veuillez sélectionner une classe valide."],
    ["role", /role/, "Veuillez sélectionner un rôle valide."],
  ];

  for (const [field, matcher, message] of fieldMatchers) {
    if (matcher.test(combined)) return { field, message };
  }

  return { message: messages[0] || "L’inscription a échoué. Veuillez réessayer." };
};
